import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './i18n/index.ts'
// first: captures the raw OAuth query before the router normalizes the URL (MCP consent, ADR 0009)
import './lib/mcp.ts'
import { ErrorFallback } from './components/ErrorFallback.tsx'
import { NotFound } from './components/NotFound.tsx'
import { ApiError, refreshSession } from './lib/api.ts'
import { installErrorReporting, noteNavigation, reportError } from './lib/error-report.ts'
import { internalPath } from './lib/internal-link.ts'
import { CACHE_BUSTER, PERSIST_MAX_AGE, persister, shouldPersistQuery } from './lib/persist.ts'
import { isTauri } from './lib/platform.ts'
import { routeTree } from './routeTree.gen.ts'
import './styles.css'

installErrorReporting()

/** Failed queries/mutations worth a report: code errors and gateway failures – not 4xx or being offline. */
function reportQueryError(where: string, key: unknown, e: unknown) {
  if (e instanceof ApiError) {
    if (e.status >= 502) reportError('http', `${e.status} ${e.message}`, { [where]: JSON.stringify(key) })
    return
  }
  if (e instanceof TypeError && /fetch|network|load failed/i.test(e.message)) return
  reportError('exception', e, { [where]: JSON.stringify(key) })
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: (e, q) => reportQueryError('query', q.queryKey, e) }),
  mutationCache: new MutationCache({
    onError: (e, _v, _c, m) => reportQueryError('mutation', m.options.mutationKey ?? 'mutation', e),
  }),
  defaultOptions: {
    // gcTime ≥ persist maxAge, otherwise restored data is garbage-collected right away
    queries: { retry: 1, refetchOnWindowFocus: false, gcTime: PERSIST_MAX_AGE, networkMode: 'offlineFirst' },
    mutations: { networkMode: 'offlineFirst' },
  },
})

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultNotFoundComponent: NotFound,
  defaultErrorComponent: ErrorFallback,
})
router.subscribe('onBeforeNavigate', (e) => noteNavigation(e.toLocation.href))

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

if (isTauri) {
  // the live UI (ADR 0014) notices deploys itself; ⌘R reloads
  void import('./lib/desktop-update.ts').then((m) => m.installDesktopUpdates())
  // links that want a new window (chat sources, screenshots, repo files) would open an unmanaged WebView window or
  // nothing at all – they go to the system browser
  document.addEventListener('click', (e) => {
    const a = e.target instanceof Element ? e.target.closest('a[href]') : null
    if (!(a instanceof HTMLAnchorElement) || a.target !== '_blank' || e.defaultPrevented) return
    e.preventDefault()
    // a link to a brain page (e.g. https://secondbrain.voluro.de/n/… in a chat answer) opens here, not in the browser
    const path = internalPath(a.href)
    if (path) return void router.history.push(path)
    void import('./lib/tauri.ts')
      .then((tauri) => tauri.openInBrowser(a.href))
      .catch((err) => reportError('exception', err))
  })
  // Mac app login: the system browser hands back brain://auth/callback?token=… (ADR 0003)
  void Promise.all([import('./lib/desktop-auth.ts'), import('./lib/tauri.ts')]).then(([desktop, tauri]) =>
    tauri.onDeepLink(async (url) => {
      const token = desktop.parseAuthCallback(url)
      if (!token || !desktop.takeLoginToken(token, globalThis.localStorage)) return
      try {
        await tauri.focusMainWindow()
        await desktop.completeDesktopLogin(token)
        await refreshSession(queryClient)
        await router.navigate({ to: '/' })
      } catch (e) {
        console.error('desktop login failed', e)
        reportError('exception', e, { during: 'desktop login' })
        await router.navigate({ to: '/login', search: { error: 'desktop' } })
      }
    }),
  )
}

const root = document.getElementById('root')
if (!root) throw new Error('#root missing')

createRoot(root).render(
  <StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: PERSIST_MAX_AGE,
        buster: CACHE_BUSTER,
        dehydrateOptions: {
          shouldDehydrateQuery: (q) => q.state.status === 'success' && shouldPersistQuery(q.queryKey),
        },
      }}
    >
      <RouterProvider router={router} />
    </PersistQueryClientProvider>
  </StrictMode>,
)

// PWA (§20.3): app-shell service worker for the web only – never inside the Tauri WebView.
if (!isTauri && import.meta.env.PROD && 'serviceWorker' in navigator) {
  void import('./lib/pwa.ts').then((m) => m.registerServiceWorker())
}
